import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { SSEClient, SSEOptions } from '../lib/sse-client';
import { useAuth } from './AuthContext';

interface SSEContextType {
  isConnected: boolean;
  addHandlers: (handlers: Partial<SSEOptions>) => void;
  removeHandlers: () => void;
}

const SSEContext = createContext<SSEContextType | undefined>(undefined);

export const useSSE = () => {
  const context = useContext(SSEContext);
  if (!context) {
    throw new Error('useSSE debe usarse dentro de SSEProvider');
  }
  return context;
};

export const SSEProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const isConnectingRef = useRef(false);
  const handlersRef = useRef<SSEOptions>({});
  const { isAuthenticated, isInitializing, token } = useAuth();
  const [client] = useState(() => {
    const API_URL = import.meta.env.VITE_API_URL || '';
    return new SSEClient(`${API_URL}/api/events`);
  });

  const addHandlers = useCallback((handlers: Partial<SSEOptions>) => {
    handlersRef.current = { ...handlersRef.current, ...handlers };
    client.connect(handlersRef.current);
  }, [client]);

  const removeHandlers = useCallback(() => {
        handlersRef.current = {};
  }, []);

  // Conectar una sola vez al montar el provider
  useEffect(() => {
    // No conectar si AuthContext aún se está inicializando
    if (isInitializing) {
        return;
    }

    // Solo conectar si el usuario está autenticado y tiene token
    if (!isAuthenticated || !token) {
        return;
    }

    // Verificar si estamos en una página pública (login, fichar, select-company)
    const pathname = window.location.pathname;

    // Páginas públicas:
    // - /area/fichar
    // - /area/login
    // - /portal/login
    // - /portal/select-company
    // - /:encryptedCode/area/login
    // - /:encryptedCode/area/fichar
    // - /:encryptedCode/portal/login
    // - /:encryptedCode/portal/select-company
    // - /company-required
    const isPublicPage =
        pathname === '/area/fichar' ||
        pathname === '/area/login' ||
        pathname === '/portal/login' ||
        pathname === '/portal/select-company' ||
        pathname === '/company-required' ||
        pathname.endsWith('/area/login') ||
        pathname.endsWith('/area/fichar') ||
        pathname.endsWith('/portal/login') ||
        pathname.endsWith('/portal/select-company');

    if (isPublicPage) {
        return;
    }

    if (isConnectingRef.current) {
        return;
    }

    isConnectingRef.current = true;

    client.connect({
        onConnect: () => {
            setIsConnected(true);
            isConnectingRef.current = false;
        },
        onDisconnect: () => {
            setIsConnected(false);
            isConnectingRef.current = false;
        },
        onError: (error) => {
            console.error('❌ SSE: Error:', error);
            setIsConnected(false);
            isConnectingRef.current = false;
        },
    });

    // Desconectar al desmontar
    return () => {
        client.disconnect();
        setIsConnected(false);
        isConnectingRef.current = false;
    };
  }, [isInitializing, isAuthenticated, token, client]);

  return (
    <SSEContext.Provider value={{ isConnected, addHandlers, removeHandlers }}>
      {children}
    </SSEContext.Provider>
  );
};
